import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Save, CheckCircle, ShoppingBag, MessageSquare, ArrowLeft } from 'lucide-react';
import Layout from './Layout';
import { useStore } from '../context/StoreContext';
import { useAlert } from '../context/AlertContext';
import './Management.css';

const Order = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryDate = queryParams.get('date');

    const { getStore, updateStore, user, stores, currentDate } = useStore();
    const { showAlert } = useAlert();
    const store = getStore(id);
    const today = currentDate;
    const targetDate = queryDate || today;

    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const isPastCutoff = currentTime.getHours() === 23 ? currentTime.getMinutes() >= 50 : currentTime.getHours() > 23;
    const isMyStore = user?.role === 'admin' || String(user?.storeId) === String(id);

    // 월간 마감 잠금 체크 - 사용자 요청으로 비활성화
    // const todayObj = new Date(today);
    // const targetObj = new Date(targetDate);
    // const isMonthLocked = (targetObj.getFullYear() < todayObj.getFullYear()) ||
    //     (targetObj.getFullYear() === todayObj.getFullYear() && targetObj.getMonth() < todayObj.getMonth());
    const isMonthLocked = false;

    const canEdit = !isMonthLocked && ((user?.role === 'admin') || (targetDate === today && isMyStore && !isPastCutoff));
    const isReadOnlyWarning = !isMonthLocked && targetDate === today && isMyStore && isPastCutoff && user?.role !== 'admin';

    const [items, setItems] = useState([]);
    const [notes, setNotes] = useState('');
    const [saveMessage, setSaveMessage] = useState(false);
    const [currentStoreName, setCurrentStoreName] = useState('');

    // 보안 권한 체크: 로그인 필수 및 본인 매장 확인
    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        // 관리자가 아니고 본인 매장 ID와 다르면 접근 제한
        if (user.role !== 'admin' && String(user.storeId) !== String(id)) {
            showAlert('자신이 담당하는 매장의 발주 현황만 관리할 수 있습니다.');
            navigate('/dashboard');
        }
    }, [user, navigate, id]);

    useEffect(() => {
        const storeData = getStore(id);
        if (!storeData) return;

        if (currentStoreName !== storeData.name) {
            setCurrentStoreName(storeData.name);
        }
        const defaultNames = ['핫도그 번', '소시지 (10개입)', '생수 500ml', '펩시 콜라 355ml', '종이컵 (L)', '케첩/머스타드 (팩)', '종이 트레이'];
        const itemNames = (storeData.items && storeData.items.length > 0) ? storeData.items : defaultNames;

        // 타겟 날짜의 기록이 있으면 가져오고, 없으면 0으로 초기화 (매일 새로운 발주를 위해)
        const history = storeData.orderHistory?.[targetDate];
        const stocks = history?.stocks || Array(itemNames.length).fill(0);
        const savedNotes = history?.notes || '';

        const initialData = itemNames.map((name, index) => {
            return {
                id: index + 1,
                name: name || `품목 ${index + 1}`,
                stock: Number(stocks[index]) || 0,
                unit: '박스'
            };
        });

        setItems(prev => {
            if (JSON.stringify(prev) !== JSON.stringify(initialData)) return initialData;
            return prev;
        });

        if (notes !== savedNotes) {
            setNotes(savedNotes);
        }
    }, [id, targetDate, getStore, currentStoreName]);

    const handleUpdate = (itemId, newStock) => {
        const val = newStock === '' ? '' : Number(newStock);
        setItems(prevItems => {
            return prevItems.map(item => {
                if (item.id === itemId) {
                    return { ...item, stock: val };
                }
                return item;
            });
        });
    };

    const handleSave = () => {
        const store = getStore(id);
        const orderHistory = store?.orderHistory || {};

        // 검증 로직: 수정 시 또는 관리자(Master 포함) 최초 입력 시 비고 필수
        const isEditing = !!orderHistory[targetDate];
        const currentUserId = (user?.id || '').toLowerCase();
        const currentUserRole = (user?.role || '').toLowerCase();
        const isAdminAccount = currentUserRole === 'admin' || currentUserId === 'master' || currentUserId.startsWith('admin');
        
        if (isEditing || isAdminAccount) {
            if (!notes || notes.trim() === '') {
                const alertMsg = isEditing 
                    ? '💡 보다 정확한 데이터 관리를 위해 하단 비고란에 구체적인 수정 사유를 입력해 주세요.\n(예: 오타 수정, 발주 수량 조정 등)'
                    : '📢 관리자 대면/대리 입력 시에는 반드시 하단 비고란에 해당 사유를 입력해 주시기 바랍니다.\n(예: 매니저 요청 대리 입력, 본사 지시 수량 입력 등)';
                showAlert(alertMsg);
                return;
            }
            if (isEditing && notes.trim() === (orderHistory[targetDate].notes || '').trim()) {
                showAlert('✍️ 이전 기록과 사유가 동일한 내용입니다. 변경된 부분에 대한 구체적인 사유를 추가로 작성해 주세요.\n(예: 사유 보충, 변경 사항 상세 기술 등)');
                return;
            }
        }

        const stocksToSave = items.map(item => Number(item.stock) || 0);
        const dailyStatus = store?.dailyStatus || {};
        const currentDayStatus = dailyStatus[targetDate] || {};

        updateStore(id, {
            orderData: { stocks: stocksToSave },
            orderHistory: {
                ...orderHistory,
                [targetDate]: {
                    stocks: stocksToSave,
                    notes: notes,
                    updatedAt: new Date().toISOString(),
                    isEdited: !!orderHistory[targetDate] || orderHistory[targetDate]?.isEdited
                }
            },
            dailyStatus: {
                ...dailyStatus,
                [targetDate]: {
                    ...currentDayStatus,
                    order: true
                }
            }
        });

        setSaveMessage(true);
        setTimeout(() => {
            setSaveMessage(false);
            // 최종 단계이므로 대시보드로 이동
            navigate('/dashboard');
        }, 2000);
    };

    const formattedDate = targetDate.replace(/-/g, '. ');

    return (
        <Layout>
            <div className="management-content">

                <div className="management-header-flex">
                    <h1>발주 관리 <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>v2.5</span> <span>- {store?.name || ''}</span></h1>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div className="mobile-store-name">{currentStoreName}</div>
                        <div className="date-display-badge">
                            <ShoppingBag size={18} />
                            <span>{formattedDate}</span>
                            {store?.orderHistory?.[targetDate]?.isEdited && (
                                <span style={{
                                    fontSize: '0.7rem',
                                    padding: '2px 6px',
                                    background: 'rgba(234, 179, 8, 0.2)',
                                    color: '#facc15',
                                    borderRadius: '4px',
                                    fontWeight: '800',
                                    marginLeft: '8px'
                                }}>
                                    수정됨
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {saveMessage && (
                    <div className="success-overlay">
                        <CheckCircle size={24} />
                        <span>발주 내역이 성공적으로 저장되었습니다!</span>
                    </div>
                )}

                <div className="card-header" style={{ marginBottom: '10px' }}>
                    <div className="status-indicator-group">
                        <div className={`submit-badge ${store?.dailyStatus?.[targetDate]?.order ? 'sales-done' : 'pending'}`}>
                            {store?.dailyStatus?.[targetDate]?.order ? <><CheckCircle size={16} /> 발주 완료</> : '발주 대기'}
                        </div>
                    </div>
                </div>

                <div className="glass-card management-card">
                    <div className="inventory-info-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                        <ShoppingBag size={20} color="#38bdf8" />
                        <p style={{ margin: 0 }}>금일 필요한 품목의 발주 수량을 입력해 주세요.</p>
                    </div>


                    {/* Mobile Card View (shown only on mobile) */}
                    <div className="inventory-mobile-list">
                        {items.map(item => (
                            <div key={item.id} className="inventory-mobile-card">
                                <div className="inventory-card-flex">
                                    <div className="inventory-card-info">
                                        <h4>{item.name}</h4>
                                        <p style={{ color: '#94a3b8' }}>단위: {item.unit}</p>
                                    </div>
                                    <div className="quantity-control">
                                        <button
                                            type="button"
                                            className="qty-btn"
                                            onClick={() => canEdit && handleUpdate(item.id, Math.max(0, (Number(item.stock) || 0) - 1))}
                                            disabled={!canEdit}
                                        >
                                            -
                                        </button>
                                        <input
                                            type="number"
                                            className="qty-input"
                                            value={item.stock}
                                            onChange={(e) => handleUpdate(item.id, e.target.value)}
                                            readOnly={!canEdit}
                                        />
                                        <button
                                            type="button"
                                            className="qty-btn"
                                            onClick={() => canEdit && handleUpdate(item.id, (Number(item.stock) || 0) + 1)}
                                            disabled={!canEdit}
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                                {Number(item.stock) > 0 && (
                                    <div style={{ marginTop: '10px', fontSize: '0.75rem', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <ShoppingBag size={12} /> 발주 목록에 포함됨
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Desktop Table View (shown only on desktop) */}
                    <div className="inventory-desktop-table recent-activities" style={{ background: 'transparent', padding: '0', border: 'none' }}>
                        <div className="activities-list is-admin">
                            <div className="activity-item header-item" style={{
                                gridTemplateColumns: '1.5fr 1.5fr 1fr 100px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                padding: '12px 20px',
                                borderRadius: '12px 12px 0 0'
                            }}>
                                <div>품목명</div>
                                <div style={{ textAlign: 'center' }}>발주 수량</div>
                                <div style={{ textAlign: 'center' }}>단위</div>
                                <div style={{ textAlign: 'center' }}>상태</div>
                            </div>
                            {items.map(item => (
                                <div key={item.id} className="activity-item row-item" style={{
                                    gridTemplateColumns: '1.5fr 1.5fr 1fr 100px',
                                    padding: '16px 20px',
                                    alignItems: 'center',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                }}>
                                    <div style={{ fontWeight: '600', color: '#f8fafc' }}>{item.name}</div>
                                    <div style={{ textAlign: 'center' }}>
                                        <div className="quantity-control" style={{ margin: '0 auto', width: 'fit-content' }}>
                                            <button type="button" className="qty-btn" onClick={() => canEdit && handleUpdate(item.id, Math.max(0, (Number(item.stock) || 0) - 1))} disabled={!canEdit}>-</button>
                                            <input type="number" className="qty-input" value={item.stock} onChange={(e) => handleUpdate(item.id, e.target.value)} readOnly={!canEdit} />
                                            <button type="button" className="qty-btn" onClick={() => canEdit && handleUpdate(item.id, (Number(item.stock) || 0) + 1)} disabled={!canEdit}>+</button>
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'center', color: '#64748b' }}>{item.unit}</div>
                                    <div style={{ textAlign: 'center' }}>
                                        <span className={`status-badge-modern ${Number(item.stock) > 0 ? 'normal' : 'pending'}`} style={{ backgroundColor: Number(item.stock) > 0 ? 'rgba(56, 189, 248, 0.1)' : 'rgba(148, 163, 184, 0.1)', color: Number(item.stock) > 0 ? '#38bdf8' : '#94a3b8' }}>
                                            {Number(item.stock) > 0 ? '발주포함' : '미발주'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="notes-section" style={{ marginTop: '24px' }}>
                        <label>
                            <MessageSquare size={18} /> 
                            발주 관련 요청사항 {store?.orderHistory?.[targetDate] ? <span style={{ color: '#ef4444', marginLeft: '4px' }}>(수정 시 필수입력)</span> : <span style={{ color: '#64748b', marginLeft: '4px' }}>(선택사항)</span>}
                        </label>
                        <textarea
                            className="notes-textarea"
                            placeholder="특이사항이나 배송 관련 요청사항을 입력해 주세요."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>

                    <button
                        className="full-width-button"
                        onClick={handleSave}
                        disabled={!canEdit}
                        style={{
                            marginTop: '24px',
                            background: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)',
                            opacity: canEdit ? 1 : 0.5
                        }}
                    >
                        <Save size={18} style={{ marginRight: '8px' }} />
                        {currentStoreName || '매장'} 발주 요청 확정
                    </button>
                </div>

                {/* Admin Exclusive: Recent Orders from Other Stores (PC Only) */}
                {user?.role === 'admin' && (
                    <div className="admin-wide-report recent-activities desktop-only" style={{ marginTop: '40px' }}>
                        <div className="activities-header">
                            <h3>타 매장 최근 발주 현황 (관리자 참조용)</h3>
                        </div>
                        <div className="activities-list is-admin">
                            <div className="activity-item header-item" style={{
                                gridTemplateColumns: '1.2fr 1fr 2fr 100px',
                                background: 'rgba(255, 255, 255, 0.03)',
                                padding: '12px 20px'
                            }}>
                                <div>매장명</div>
                                <div>날짜</div>
                                <div>주요 품목 현황</div>
                                <div>상태</div>
                            </div>
                            {stores.filter(s => s.id !== id).slice(0, 5).map(s => {
                                const lastOrder = s.orderHistory ? Object.entries(s.orderHistory).sort((a, b) => b[0].localeCompare(a[0]))[0] : null;
                                return (
                                    <div key={s.id} className="activity-item row-item" style={{
                                        gridTemplateColumns: '1.2fr 1fr 2fr 100px',
                                        padding: '16px 20px',
                                        borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                                    }}>
                                        <div style={{ fontWeight: '700' }}>{s.name}</div>
                                        <div style={{ color: '#94a3b8' }}>{lastOrder ? lastOrder[0] : '-'}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                            {lastOrder ? `총 ${lastOrder[1].stocks.filter(v => v > 0).length}종 품목 발주됨` : '최근 내역 없음'}
                                        </div>
                                        <div>
                                            {s.dailyStatus?.[targetDate]?.order ? (
                                                <span style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>완료</span>
                                            ) : (
                                                <span style={{ color: '#f97316', background: 'rgba(249, 115, 22, 0.1)', padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem' }}>대기</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Order;
